import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Banner } from '../components/states';
import { useI18n } from '../i18n/I18nProvider';

export function LoginPage(): JSX.Element {
  const { login, verifyMfa } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tenantSlug, setTenantSlug] = useState('acme');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // When the first step returns a challenge, we switch to the second-factor form.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const step = await login(tenantSlug, email, password);
      if (step.mfaRequired) {
        setChallenge(step.challenge);
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    } finally {
      setSubmitting(false);
    }
  };

  const onVerify = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!challenge) return;
    setSubmitting(true);
    setError(null);
    try {
      await verifyMfa(challenge, code);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    } finally {
      setSubmitting(false);
    }
  };

  if (challenge) {
    return (
      <div className="login">
        <form className="card login__card" onSubmit={(e) => void onVerify(e)}>
          <h1 className="login__brand">
            <img
              className="login__logo login__logo--light"
              src="/brand/connaro-logo-light.png"
              alt="Connaro"
            />
            <img
              className="login__logo login__logo--dark"
              src="/brand/connaro-logo-dark.png"
              alt="Connaro"
            />
          </h1>
          <p className="login__tagline">{t('mfa.login.prompt')}</p>
          {error ? <Banner variant="error">{error}</Banner> : null}
          <label className="field">
            <span>{t('mfa.login.code')}</span>
            <input
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? t('auth.signingIn') : t('mfa.login.verify')}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setChallenge(null);
              setCode('');
              setError(null);
            }}
          >
            {t('mfa.login.back')}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="login">
      <form className="card login__card" onSubmit={(e) => void onSubmit(e)}>
        <h1 className="login__brand">
          <img
            className="login__logo login__logo--light"
            src="/brand/connaro-logo-light.png"
            alt="Connaro"
          />
          <img
            className="login__logo login__logo--dark"
            src="/brand/connaro-logo-dark.png"
            alt="Connaro"
          />
        </h1>
        <p className="login__tagline">{t('app.tagline')}</p>
        {error ? <Banner variant="error">{error}</Banner> : null}
        <label className="field">
          <span>Tenant</span>
          <input value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)} required />
        </label>
        <label className="field">
          <span>{t('auth.fields.email')}</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>{t('auth.fields.password')}</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <button type="submit" className="btn btn--primary" disabled={submitting}>
          {submitting ? t('auth.signingIn') : t('auth.signIn')}
        </button>
      </form>
    </div>
  );
}
